<template>
  <section
    :id="isWeapons ? 'arsenal' : 'heroes'"
    class="lm-section"
    :class="{ 'lm-arsenal': isWeapons }"
  >
    <div class="container-wrapper">
      <p class="lm-eyebrow">
        {{ isWeapons ? "FORGE YOUR BUILD" : "THE COMPLETE ROSTER" }}
      </p>
      <h2>
        {{
          isWeapons
            ? "Discover your arsenal."
            : "Seven heroes. Find your strength."
        }}
      </h2>
      <p class="lm-intro">
        {{
          isWeapons
            ? "From a first blade to divine gear. Explore all nine distinct weapons in build 1.0.0, with the equipment rules that shape your choices."
            : "Start with Kael, then unlock new strengths with earned crystals or Arena coins. All seven heroes below are included in build 1.0.0."
        }}
      </p>
      <div
        v-if="isWeapons"
        class="lm-filters"
        role="group"
        aria-label="Filter weapons"
      >
        <button
          v-for="filter in filters"
          :key="filter"
          type="button"
          :aria-pressed="category === filter"
          @click="filterBy(filter)"
        >
          {{ filter }}
        </button>
        <span>{{ visible.length }} weapons</span>
      </div>
      <div class="lm-collection-stage">
        <div class="lm-collection-art">
          <span class="lm-art-ring" aria-hidden="true"></span>
          <picture
            ><source
              :srcset="art + selected.image + '.webp'"
              type="image/webp" />
            <img
              :key="selected.id"
              :src="art + selected.image + '.png'"
              :width="selected.width"
              :height="selected.height"
              :alt="selected.name + ' — original game artwork'"
              loading="lazy"
          /></picture>
        </div>
        <div class="lm-collection-copy" aria-live="polite" aria-atomic="true">
          <p class="lm-eyebrow">
            {{
              isWeapons
                ? selected.category + " · " + selected.rarity
                : selected.role + " · " + selected.title
            }}
          </p>
          <h3>{{ selected.name }}</h3>
          <p>{{ isWeapons ? weaponDescription : selected.body }}</p>
          <template v-if="isWeapons">
            <dl>
              <dt>Compatible heroes</dt>
              <dd>{{ compatibleNames }}</dd>
              <dt>Build characteristics</dt>
              <dd>{{ bonusDescription }}</dd>
              <dt>Equipment slot</dt>
              <dd>Main weapon · {{ selected.role }} equipment</dd>
            </dl>
          </template>
          <template v-else
            ><p class="lm-passive">{{ selected.passive }}</p>
            <p class="lm-note">
              {{
                selected.id === "kael"
                  ? "Owned from the start."
                  : "Unlock with earned crystals or Arena coins."
              }}
              Develop individual levels, skills and equipment.
            </p></template
          >
          <span class="lm-collection-label">In the 1.0.0 test build</span>
        </div>
      </div>
      <div
        class="lm-collection-picker"
        :class="{ 'lm-weapon-picker': isWeapons }"
        role="group"
        :aria-label="isWeapons ? 'Choose a weapon' : 'Choose a hero'"
      >
        <button
          v-for="item in visible"
          :key="item.id"
          type="button"
          :aria-pressed="selected.id === item.id"
          @click="selectedId = item.id"
        >
          <picture
            ><source :srcset="art + item.image + '-thumb.webp'" type="image/webp" />
            <img
              :src="art + item.image + '.png'"
              :width="item.width"
              :height="item.height"
              alt=""
              loading="lazy"
          /></picture>
          <span>{{ item.name }}</span
          ><small>{{
            isWeapons ? item.rarity + " · " + item.role : item.role
          }}</small>
        </button>
      </div>
      <p class="lm-note">
        {{
          isWeapons
            ? "One artwork per weapon; upgrade levels are not separate weapons. Rarity labels are Iron, Mystic, Heroic and Divine for this collection. Equipment can be upgraded and evolved with resources and progression requirements. Shields, armour and accessories are separate equipment types."
            : "All seven heroes are in the 1.0.0 test build. Evolution appearances and alternate portraits are variations of these heroes."
        }}
      </p>
    </div>
  </section>
</template>
<script>
import inventory from "./lodomelix-inventory.json";
const bonusNames = {
  attackBonus: "physical attack",
  strengthBonus: "strength",
  magicBonus: "magic attack",
  criticalChanceBonus: "critical chance",
  skillDamageBonus: "skill damage",
  speedBonus: "speed",
  cooldownReductionBonus: "cooldown reduction",
};
export default {
  props: { isWeapons: { type: Boolean, default: false } },
  data() {
    return {
      art: "/games/ludomelix/release-1/",
      selectedId: this.isWeapons ? "astral_archmage_staff" : "kael",
      category: "All",
      filters: ["All", "Blades", "Staves"],
    };
  },
  computed: {
    items() {
      return this.isWeapons ? inventory.weapons : inventory.heroes;
    },
    visible() {
      return this.items.filter(
        (i) =>
          this.category === "All" ||
          i.category === (this.category === "Staves" ? "Staff" : "Blade"),
      );
    },
    selected() {
      return this.items.find((i) => i.id === this.selectedId) || this.items[0];
    },
    compatibleNames() {
      return inventory.heroes
        .filter((h) => h.role === this.selected.role)
        .map((h) => h.name)
        .join(" · ");
    },
    bonusDescription() {
      return (
        Object.keys(this.selected.bonuses || {})
          .map((k) => bonusNames[k] || k)
          .join(", ") + "."
      );
    },
    weaponDescription() {
      const descriptions = {
        iron_sword:
          "A straightforward early blade with a physical attack bonus.",
        basic_sword:
          "A starter blade with attack, strength and critical-chance bonuses.",
        mystic_sword:
          "A blade that adds magic, critical chance and skill damage alongside physical attack.",
        heroic_sword:
          "A stronger blade with attack, strength, critical-chance and skill-damage bonuses.",
        abyss_emperor_sword:
          "An Abyss Tower weapon with physical attack and skill-damage bonuses.",
        eclipse_sovereign_blade:
          "A divine blade found in Eclipse Relic Chests.",
        veridia_super_staff:
          "A support-class staff with physical attack and speed bonuses.",
        royal_vanguard_sword:
          "A warrior-class blade with physical attack and speed bonuses.",
        astral_archmage_staff:
          "A mage-class staff focused on magic attack, with skill damage and cooldown reduction.",
      };
      return descriptions[this.selected.id];
    },
  },
  methods: {
    filterBy(filter) {
      this.category = filter;
      if (!this.visible.some((i) => i.id === this.selectedId))
        this.selectedId = this.visible[0].id;
    },
  },
};
</script>
